import { IAuditDto, ID, IVisibleToItem, LocalizationSet } from '@clean-code/models/common';
import { IDashboardWidgetDetailDto } from '@clean-code/shared/components/ui-dashboard';
import { IDashboardDetailDto, IDashboardPanelPreviewDto } from './dashboard.models';

export interface DashboardResponse {
  name: LocalizationSet;
  description?: LocalizationSet;
  color: string;
  shared: boolean;
  visibleTo: string | string[];
  // visibleToObj: IVisibleToItem[];
  widgets: DashboardWidget[];
  tenant: string;
  createdBy: string;
  updatedBy: string;
  createdDate: Date;
  updatedDate: string;
  id: number;
  creator: IAuditDto;
}

function normalizeVisibleToKeys(
  visibleTo: string | string[] | IVisibleToItem[] | null | undefined,
): string[] {
  if (visibleTo == null) {
    return [];
  }
  if (Array.isArray(visibleTo)) {
    return visibleTo
      .map((item) => (typeof item === 'string' ? item : item.key))
      .filter(Boolean);
  }
  return visibleTo.split(',').filter(Boolean);
}

function toSharingState(
  shared: boolean,
  visibleTo: string | string[] | IVisibleToItem[] | null | undefined,
): { shared: boolean; visibleTo: string[] } {
  const keys = normalizeVisibleToKeys(visibleTo);

  return {
    shared: shared || keys.length > 0,
    visibleTo: keys,
  };
}

export interface DashboardWidget {
  dashboardId: ID;
  sizeY: number;
  sizeX: number;
  positionY: number;
  positionX: number;
  widget: WidgetResponse;
}

export interface Widget {
  createdDate: Date;
  id: ID;
  name: LocalizationSet;
  description?: LocalizationSet;
  type: string;
  color: string;
  shared: boolean;
  visibleTo: IVisibleToItem[];
  config: string;
  creator: Creator;
}

export interface Creator {
  id: string;
  fullName: string;
}

export interface WidgetResponse {
  createdDate: Date;
  id: ID;
  name: LocalizationSet;
  description?: LocalizationSet;
  type: string;
  color: string;
  shared: boolean;
  visibleToObj: IVisibleToItem[];
  config: string;
  createdBy: ID;
  creator: IAuditDto;
}

export const dashboardResponseToDashboardDetailDto = (
  value: DashboardResponse
): IDashboardDetailDto => {
  const sharing = toSharingState(value.shared, value.visibleTo);

  return {
    id: value.id,
    name: value.name,
    description: value.description,
    color: value.color,
    shared: sharing.shared,
    visibleTo: sharing.visibleTo,
    widgets: value.widgets.map((w) =>
      widgetResponseToDashboardWidgetDetailDto(w, value.tenant.split(';'))
    ),

    tenant: value.tenant.split(';'),
    creator: {
      id: value.creator.id,
      fullName: value.creator.fullName,
      date: value.createdDate
    }
  };
};

export const dashboardResponseToDashboard = (
  value: DashboardResponse
): IDashboardPanelPreviewDto => {
  const sharing = toSharingState(value.shared, value.visibleTo);

  return {
    id: value.id,
    name: value.name,
    description: value.description,
    color: value.color,
    shared: sharing.shared,
    visibleTo: sharing.visibleTo,
    tenant: value.tenant.split(';'),
    creator: {
      id: value.creator.id,
      fullName: value.creator.fullName,
      date: value.createdDate
    }
  };
};

export const widgetResponseToDashboardWidgetDetailDto = (
  w: DashboardWidget,
  tenant: string[]
): IDashboardWidgetDetailDto => {
  return {
    id: w.widget.id,
    dashboardId: w.dashboardId,
    x: w.positionX ?? 0,
    y: w.positionY ?? 0,
    cols: w.sizeX ?? 5,
    rows: w.sizeY ?? 5,
    type: w.widget.type,
    name: w.widget.name,
    shared: w.widget.shared,
    config: w.widget.config,
    visibleTo: w.widget.visibleToObj,
    color: w.widget.color,
    tenant: tenant,
    creator: {
      id: w.widget.creator.id,
      fullName: w.widget.creator.fullName,
      date: w.widget.createdDate
    },
    minItemCols: 0,
    minItemRows: 0
  };
};
